/**
 * Compact Batch Filters Component
 * Inline filter controls to display beside search bar
 *
 * @author SAUBHAGYA Development Team
 * @since US-BATCH-005
 */

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuSeparator,
  DropdownMenuLabel,
  DropdownMenuTrigger,
  DropdownMenuItem
} from '@/components/ui/dropdown-menu';
import { useState } from 'react';
import type { BatchFilters } from '@/types/availableBatches';
import { EXPIRY_RANGE_OPTIONS } from '@/types/availableBatches';
import { ProductType, QualityGrade, ProductTypeConfig, QualityGradeConfig } from '@/types/inventory';
import {
  Filter,
  Award,
  X
} from 'lucide-react';
import { cn } from '@/lib/utils';

interface CompactBatchFiltersProps {
  filters: BatchFilters;
  onFiltersChange: (filters: BatchFilters) => void;
  onReset: () => void;
  activeFilterCount: number;
  className?: string;
}

export function CompactBatchFilters({
  filters,
  onFiltersChange,
  onReset,
  activeFilterCount,
  className
}: CompactBatchFiltersProps) {
  const [isQualityOpen, setIsQualityOpen] = useState(false);

  const handleProductTypeChange = (value: string) => {
    onFiltersChange({
      ...filters,
      productType: value as ProductType | 'ALL'
    });
  };

  const handleQualityGradeToggle = (grade: QualityGrade) => {
    const newGrades = filters.qualityGrades.includes(grade)
      ? filters.qualityGrades.filter(g => g !== grade)
      : [...filters.qualityGrades, grade];

    onFiltersChange({
      ...filters,
      qualityGrades: newGrades
    });
  };

  const handleExpiryRangeChange = (value: string) => {
    onFiltersChange({
      ...filters,
      expiryRange: value as BatchFilters['expiryRange']
    });
  };

  return (
    <div className={cn('flex flex-wrap items-center gap-3', className)}>
      {/* Product Type Filter */}
      <Select value={filters.productType} onValueChange={handleProductTypeChange}>
        <SelectTrigger className="w-[180px]">
          <SelectValue placeholder="All Products" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="ALL">All Product Types</SelectItem>
          {Object.entries(ProductTypeConfig).map(([key, config]) => (
            <SelectItem key={key} value={key}>
              <span className="flex items-center gap-2">
                <span>{config.icon}</span>
                <span>{config.label}</span>
              </span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* Quality Grade Filter (Dropdown) */}
      <div className="relative">
        <Button
          variant="outline"
          size="sm"
          className={cn(
            'gap-2',
            filters.qualityGrades.length > 0 && 'border-blue-300 bg-blue-50'
          )}
          onClick={() => setIsQualityOpen(!isQualityOpen)}
        >
          <Award className="h-4 w-4" />
          Quality
          {filters.qualityGrades.length > 0 && (
            <Badge variant="secondary" className="ml-1 h-5">
              {filters.qualityGrades.length}
            </Badge>
          )}
        </Button>

        {isQualityOpen && (
          <div className="absolute top-full left-0 mt-1 w-56 bg-white border rounded-md shadow-lg z-50">
            <div className="p-2 font-medium text-sm">Quality Grades</div>
            <div className="border-t"></div>
            {Object.entries(QualityGradeConfig).map(([key, config]) => (
              <label
                key={key}
                className={cn(
                  'flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-gray-100',
                  filters.qualityGrades.includes(key as QualityGrade) && 'bg-blue-50'
                )}
              >
                <input
                  type="checkbox"
                  checked={filters.qualityGrades.includes(key as QualityGrade)}
                  onChange={(e) => {
                    handleQualityGradeToggle(key as QualityGrade);
                  }}
                  className="w-4 h-4"
                />
                <div>
                  <span className="font-medium">Grade {key}</span>
                  <span className="text-xs text-gray-500 ml-2">({config.description})</span>
                </div>
              </label>
            ))}
          </div>
        )}
      </div>

      {/* Expiry Range Filter */}
      <Select value={filters.expiryRange} onValueChange={handleExpiryRangeChange}>
        <SelectTrigger className="w-[180px]">
          <SelectValue placeholder="Expiry Range" />
        </SelectTrigger>
        <SelectContent>
          {EXPIRY_RANGE_OPTIONS.map(option => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* Active Filter Indicator & Clear Button */}
      {activeFilterCount > 0 && (
        <div className="flex items-center gap-2">
          <Badge variant="destructive" className="gap-1">
            <Filter className="h-3 w-3" />
            {activeFilterCount} active
          </Badge>
          <Button
            variant="ghost"
            size="sm"
            onClick={onReset}
            className="h-8 w-8 p-0 text-gray-500 hover:text-red-600"
            title="Clear all filters"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      )}
    </div>
  );
}
